import { formatCents, formatPercent, roundToCashStep, tt } from '@agos/shared'

import type { CustomerUser } from './use-customer-auth'

/**
 * What comes off a customer's basket, and in what order — FB-C04.
 *
 * `Money` draws the line the server works to, and the customer checkout has it backwards:
 *
 * - `cents()` — *"an amount that is arithmetic rather than cash: a line price, a subtotal, a discount"*;
 * - `payable()` — *"what the customer actually pays, rounded so it can be handed over in coins."*
 *
 * So a **discount keeps its cent** and only the **payable total** takes the five-cent step. Legacy
 * rounds the coupon, the referral and the personal discount to five cents (`Math.round(x * 20) / 20`)
 * and happy hours and Black Friday to one — five sources, two rules, three of them disagreeing with the
 * server. A 10 % coupon on 23,30 € previews 2,35 € against a charged 2,33 €.
 *
 * It matters because `OrderCreationService` recomputes every discount for a web order — *"almost
 * everything it is told is treated as a suggestion"* — so this is a preview of a number it does not
 * control, and a preview knowably wrong by two cents is worse than none.
 *
 * **The stacking rule is the server's**, and legacy mirrors it deliberately: a non-stackable personal
 * discount **replaces** every other source. That is kept, including saying so where a coupon would be
 * typed, because it is the page at its best.
 */

export interface DiscountSources {
  couponCents: number
  referralCents: number
  pointsCents: number
  happyHoursCents: number
  /**
   * Zero on every screen today, and deliberately kept: `OrderCampaigns` decides Black Friday
   * server-side, so the *order* is discounted whether or not the summary mentions it. This holds the
   * stacking position for the banner. See FB-C44.
   */
  blackFridayCents: number
}

export const NO_DISCOUNTS: DiscountSources = {
  couponCents: 0,
  referralCents: 0,
  pointsCents: 0,
  happyHoursCents: 0,
  blackFridayCents: 0,
}

/**
 * A percentage of a basket, to the cent — never to the coin.
 *
 * Uncapped, like `WebOrderDiscounts.percentOf`, which is plain `Money.cents(amount × percent / 100)`.
 * Capping here as well would look harmless and diverge: the server caps **once**, on the sum of every
 * source, and against subtotal *plus delivery*. Two caps at different ceilings is how a preview drifts.
 */
export function percentOf(subtotalCents: number, percent: number): number {
  if (!Number.isFinite(percent) || percent <= 0) return 0
  return Math.round((subtotalCents * percent) / 100)
}

export function personalDiscountCents(user: CustomerUser | null, subtotalCents: number): number {
  if (user?.personalDiscountType == null || subtotalCents <= 0) return 0
  const value = user.personalDiscountValue ?? 0
  return user.personalDiscountType === 'percent'
    ? percentOf(subtotalCents, value)
    : Math.min(subtotalCents, Math.round(value * 100))
}

/**
 * True when the account's own discount is the only one that applies.
 *
 * `personalDiscountStackable` defaults to false, and the server enforces it at order creation. Showing
 * a coupon line that will not be honoured is the thing to avoid.
 */
export function replacesEverything(user: CustomerUser | null, subtotalCents: number): boolean {
  return (
    user?.personalDiscountType != null &&
    user.personalDiscountStackable !== true &&
    personalDiscountCents(user, subtotalCents) > 0
  )
}

/**
 * Every source added up, then capped once — the shape of `WebOrderDiscounts.compute`.
 *
 * **The ceiling is subtotal *plus delivery***, which is the server's `min(subtotal.add(deliveryCost))`
 * and not the subtotal alone. It is a real difference rather than a pedantic one: a 25 € voucher on
 * 22 € of food with 3 € delivery is worth the whole 25 € to the server and would preview as 22 €.
 *
 * Delivery is therefore required rather than optional. An optional `deliveryCents = 0` would silently
 * reintroduce the tighter ceiling at every call site that forgot it, which is precisely the class of
 * mistake FB-C04 is.
 */
export function totalDiscountCents(
  user: CustomerUser | null,
  sources: DiscountSources,
  basket: { subtotalCents: number; deliveryCents: number },
): number {
  const { subtotalCents, deliveryCents } = basket
  const personal = personalDiscountCents(user, subtotalCents)
  // Exclusive mode wipes everything above it, server-side. It is still capped: the ceiling applies to
  // whatever survives, not only to a stack.
  const running = replacesEverything(user, subtotalCents)
    ? personal
    : sources.couponCents +
      sources.referralCents +
      sources.pointsCents +
      sources.happyHoursCents +
      sources.blackFridayCents +
      personal
  return Math.min(subtotalCents + deliveryCents, Math.max(0, running))
}

/**
 * What is handed over: goods, plus delivery, plus deposits, less the discount — and only then rounded.
 *
 * **The deposits are FB-C05.** `OrderCreationService` adds surcharges to the total and deliberately not
 * to the subtotal: *"leaving them out of the total means the deposit line prints on the receipt and
 * nobody collects it, which costs the restaurant 15 cents a can."* The customer app has no concept of
 * them, which was harmless at zero rows and stopped being harmless when `/surcharges` shipped.
 */
export function payableCents(input: {
  subtotalCents: number
  deliveryCents: number
  depositCents: number
  discountCents: number
}): number {
  const raw =
    input.subtotalCents + input.deliveryCents + input.depositCents - input.discountCents
  return roundToCashStep(Math.max(0, raw))
}

/** Said where a coupon would be typed, when the account's own discount has taken its place. */
export function replacedNote(): string {
  return tt('Máte aktívnu trvalú zľavu - kupóny sa pre tento účet neuplatňujú.')
}

/** Said under the total when the five-cent step moved it. */
export function roundingNote(exactCents: number, payable: number): string {
  if (exactCents === payable) return ''
  return `${tt('Zaokrúhlené na 5 centov z')} ${formatCents(exactCents)}`
}

/**
 * The account's own discount, named, and whatever else is left — `FRONTEND.md` §9.65.
 *
 * The owner reported that a permanent discount is not shown at checkout. It **is** applied: it goes
 * through {@link totalDiscountCents} like every other source, so the total has always been right.
 * What the customer saw was one unlabelled *Zľava* line, which tells somebody an amount came off and
 * not that their account carries a standing benefit.
 *
 * **The two parts always sum to what was actually taken off**, which is the reason this is a function
 * rather than two calls at the call site. `totalDiscountCents` **caps** the stack against subtotal
 * plus delivery, so the raw personal figure can exceed what was allowed — printing it unclamped
 * beside a smaller total is a summary that does not add up, and a customer checking the arithmetic is
 * exactly the customer who has a discount worth checking.
 */
export function discountBreakdown(
  user: CustomerUser | null,
  subtotalCents: number,
  appliedCents: number,
): { personalCents: number; otherCents: number } {
  const personal = Math.min(personalDiscountCents(user, subtotalCents), Math.max(0, appliedCents))
  return { personalCents: personal, otherCents: Math.max(0, appliedCents - personal) }
}

/** How to name the account's discount — the percentage where it is one, the sum where it is not. */
export function personalDiscountLabel(user: CustomerUser | null): string {
  const amount = personalDiscountAmount(user)
  return amount === '' ? tt('Vaša trvalá zľava') : `${tt('Vaša trvalá zľava')} ${amount}`
}

/**
 * The figure alone — §9.246.
 *
 * The profile prints it large under its own heading and the checkout prints it inside a sentence, so
 * the *percent-or-euros* rule is written here once and read twice. `formatPercent` rather than a
 * template string: a Slovak screen writes 30,5 % with a comma and `toFixed` writes a dot, which is
 * the lint rule that exists because it had shipped before.
 *
 * A fixed discount is money and goes through `formatCents`, so it carries the € the same way every
 * other sum on the page does.
 */
export function personalDiscountAmount(user: CustomerUser | null): string {
  const value = user?.personalDiscountValue ?? 0
  if (user?.personalDiscountType === 'percent') {
    return formatPercent(value, Number.isInteger(value) ? 0 : 1)
  }
  if (user?.personalDiscountType === 'fixed') return formatCents(Math.round(value * 100))
  return ''
}
